/**
 * src/lib/cookieConsent.ts
 *
 * Cookie Consent — fonte síncrona única (LGPD).
 *
 * Storage: localStorage (não autenticado-friendly + leitura síncrona).
 * Server-side: user_legal_acceptances (audit trail) — sincronizado pelo
 * hook useCookieConsent quando usuária está autenticada.
 *
 * Comportamento "forward-only": antes do consent, ferramentas com 3rd-party
 * (Sentry, GA4-removido, UTM) ficam OFF. Quando aceito, passam a rodar
 * a partir desse momento. Quando recusado, permanecem OFF; analytics_events
 * interno continua (não envia para fora).
 *
 * Versionamento: alinhado com CURRENT_AI_DISCLAIMER_VERSION (Item 2.4).
 * "v1-placeholder" sinaliza que wording aguarda revisão jurídica. Quando
 * advogada entregar, trocar para "v1-final-juridica" — força reaceite via
 * UNIQUE(user_id, acceptance_kind, version) e re-renderiza o banner para
 * todas usuárias automaticamente.
 */

export const CURRENT_COOKIE_VERSION = "v1-placeholder";

const STATUS_KEY = "m360:cookie_consent";       // "accepted" | "rejected"
const VERSION_KEY = "m360:cookie_consent_v";    // versão aceita
const CHANGE_EVENT = "m360:cookie_consent_changed";

export type CookieConsentStatus = "accepted" | "rejected" | null;

/** Lê status atual. Retorna null se nunca decidiu OU versão antiga. */
export function getCookieConsentStatus(): CookieConsentStatus {
  try {
    const status = localStorage.getItem(STATUS_KEY);
    const version = localStorage.getItem(VERSION_KEY);
    if (!status || version !== CURRENT_COOKIE_VERSION) return null;
    return status === "accepted" || status === "rejected" ? status : null;
  } catch {
    return null;
  }
}

/** True quando ferramentas 3rd-party (Sentry, UTM) podem rodar. */
export function isCookieConsentAccepted(): boolean {
  return getCookieConsentStatus() === "accepted";
}

/** True quando precisa mostrar banner (sem decisão na versão atual). */
export function needsCookieConsent(): boolean {
  return getCookieConsentStatus() === null;
}

/** Persiste decisão + dispara evento global para hooks reagirem. */
export function setCookieConsent(status: "accepted" | "rejected"): void {
  try {
    localStorage.setItem(STATUS_KEY, status);
    localStorage.setItem(VERSION_KEY, CURRENT_COOKIE_VERSION);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { status } }));
  } catch {
    /* ignora erro de storage (modo privado restrito) */
  }
}

/**
 * Reabre o banner apagando a decisão persistida.
 *
 * Forward-only: ferramentas 3rd-party (Sentry, Meta Pixel) já em execução na
 * sessão continuam até o próximo reload — só o próximo boot respeita a nova
 * decisão. Aceito como tradeoff (padrão comum em CMPs).
 */
export function reopenCookieConsent(): void {
  try {
    localStorage.removeItem(STATUS_KEY);
    localStorage.removeItem(VERSION_KEY);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { status: null } }));
  } catch {
    /* ignora erro de storage */
  }
}

/** Subscribe a mudanças de consent. Retorna unsubscribe. */
export function onCookieConsentChange(
  cb: (status: "accepted" | "rejected" | null) => void,
): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail as { status: "accepted" | "rejected" | null };
    cb(detail.status);
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

